// ============================================================================
// FSA NOTE MODAL — DISCLOSURE GRID GROUP HEADER
// ============================================================================
// Renders one or two header rows depending on whether any column carries
// a `group` field. Both the variable-grid and fixed-grid disclosure
// renderers share this so the look stays consistent across all four
// Share Capital disclosure layouts (Authorised, Equity Recon,
// Preference Recon, Shareholders > 5%) and any future grouped grids.
//
// Layout rule:
//   - Columns with the SAME `group` value (consecutive) render under a
//     spanning top header cell labelled with the group.
//   - Columns with NO group span both rows (rowSpan=2). Avoids the
//     "blank top cell + label below" look the FsaTemplatePdfService uses
//     for its grouped headers.
//   - When NO column has a group, falls back to a single header row.

import React from 'react';

export interface ColumnLike {
  code: string;
  label: string;
  align?: string;
  group?: string;
  is_computed?: boolean;
  formula?: string;
}

interface Props<T extends ColumnLike> {
  columns: T[];
  /** Optional extra trailing cell (e.g. row-delete column on variable grids). */
  trailingCellWidth?: number;
  /** Renders the label cell content. Caller controls iconography (e.g. ƒ chip on computed). */
  renderLabel: (col: T) => React.ReactNode;
  /** Style supplier for an individual column header cell — re-uses the caller's th palette. */
  cellStyle: (col: T) => React.CSSProperties;
  /** Style supplier for the SPANNING group label cell. Falls back to first member's cellStyle. */
  groupCellStyle?: (group: string, columns: T[]) => React.CSSProperties;
}

/**
 * Walk columns and emit consecutive (group, members) runs. Ungrouped
 * members surface as { group: undefined, members: [col] } so the
 * renderer can render them with rowSpan=2.
 */
function computeRuns<T extends ColumnLike>(columns: T[]): Array<{ group?: string; members: T[] }> {
  const runs: Array<{ group?: string; members: T[] }> = [];
  for (const c of columns) {
    const last = runs[runs.length - 1];
    if (last && (last.group ?? '') === (c.group ?? '')) {
      last.members.push(c);
    } else {
      runs.push({ group: c.group, members: [c] });
    }
  }
  return runs;
}

export function DisclosureGroupHeader<T extends ColumnLike>({
  columns,
  trailingCellWidth,
  renderLabel,
  cellStyle,
  groupCellStyle,
}: Props<T>): React.ReactElement {
  const hasGroups = columns.some(c => !!c.group && c.group.trim());

  // No groups → flat 1-row header. Caller sees a single <tr> children list.
  if (!hasGroups) {
    return (
      <tr>
        {columns.map(c => (
          <th key={c.code} style={cellStyle(c)}>
            {renderLabel(c)}
          </th>
        ))}
        {trailingCellWidth != null && <th style={{ width: trailingCellWidth }} />}
      </tr>
    );
  }

  const runs = computeRuns(columns);

  return (
    <>
      <tr>
        {(() => {
          // R1 cells: one rowSpan-2 per ungrouped MEMBER (so a run of two
          // ungrouped columns like Shareholder Name + Class of Share each
          // get their own header cell), one spanning banner per group.
          const cells: React.ReactElement[] = [];
          for (let i = 0; i < runs.length; i++) {
            const run = runs[i];
            if (!run.group) {
              for (const col of run.members) {
                cells.push(
                  <th
                    key={`u-${col.code}`}
                    rowSpan={2}
                    style={{ ...cellStyle(col), verticalAlign: 'middle' }}
                  >
                    {renderLabel(col)}
                  </th>,
                );
              }
              continue;
            }
            const baseStyle = groupCellStyle
              ? groupCellStyle(run.group, run.members)
              : { ...cellStyle(run.members[0]), textAlign: 'center' as const };
            cells.push(
              <th
                key={`g-${i}-${run.group}`}
                colSpan={run.members.length}
                style={{ ...baseStyle, textAlign: 'center' }}
              >
                {run.group}
              </th>,
            );
          }
          return cells;
        })()}
        {trailingCellWidth != null && <th rowSpan={2} style={{ width: trailingCellWidth }} />}
      </tr>
      <tr>
        {(() => {
          // flatMap over runs would tangle the React.ReactNode return type;
          // a plain loop keeps each emitted cell as a JSX.Element so React's
          // strict typing accepts the array.
          const cells: React.ReactElement[] = [];
          for (const run of runs) {
            if (!run.group) continue;
            for (const col of run.members) {
              cells.push(
                <th key={`sub-${col.code}`} style={cellStyle(col)}>
                  {renderLabel(col)}
                </th>,
              );
            }
          }
          return cells;
        })()}
      </tr>
    </>
  );
}

export default DisclosureGroupHeader;
